import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import PromptCounter from "./Components/PromptCounter";
import AddEntry from "./Components/AddEntry";
import LobbyAccordions from "./Components/LobbyAccordions.tsx";
import { getBlockedSuggestions } from "./utils/blockedSuggestions";
import settingsIcon from "./photos/settings.svg";

interface PublicPlayerData {
  sessionId: string;
  name: string;
}

interface AIRoomLobbyProps {
  roomName: string;
  sessionId: string;
  socket: any;
  players: PublicPlayerData[];
  promptCount: number;
  customPromptCount: number;
  setupDone: boolean;
  isGenerating: boolean;
  isOwner: boolean;
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
  containerHeight: number;
  setRoomState: (state: string) => void;
  setAIRoomState: (state: string) => void;
  onOpenSettings: () => void;
  onStart: () => void;
}

// Mirrors the classic lobby (NewRoom.js preStartRender) so both room types
// look the same before the game starts.
function AIRoomLobby({
  roomName,
  sessionId,
  socket,
  players,
  promptCount,
  customPromptCount,
  setupDone,
  isGenerating,
  isOwner,
  showInfoMessage,
  containerHeight,
  setRoomState,
  onOpenSettings,
  onStart,
}: AIRoomLobbyProps) {
  const { t } = useTranslation();
  const [roomInput, setRoomInput] = useState("");
  const textArea = useRef<HTMLTextAreaElement>(null);

  const blockedSuggestions = getBlockedSuggestions(t);

  const add = () => {
    if (roomInput !== "") {
      if (blockedSuggestions.includes(roomInput)) {
        showInfoMessage("warning", t("suggestion-incomplete"), false);
        return;
      }
      socket.emit("ai room add prompt", {
        roomName,
        sessionId,
        prompt: roomInput,
      });
      setRoomInput("");
      showInfoMessage("", t("added"), true, "short");
    }
    document.getElementById("page")?.scrollIntoView();
  };

  const addName = (name: string) => {
    socket.emit("ai room add name", { roomName, sessionId, name });
  };

  const updateName = (name: string) => {
    socket.emit("ai room update name", { roomName, sessionId, name });
  };

  const kick = (targetSessionId: string) => {
    socket.emit("ai room kick", { roomName, sessionId, targetSessionId });
  };

  const back = () => {
    setRoomState("start screen");
    socket.emit("leave room", roomName);
  };

  const startLabel = isGenerating
    ? t("ai.generating")
    : setupDone || promptCount > 0
      ? t("classic.continueGame")
      : t("start");

  return (
    <div className="App-header">
      <div className="App-header">
        <PromptCounter numPrompts={customPromptCount} />
        <div>
          <div className="container-row lift-topbar">
            <div className="container-row-start">
              <div
                className="button-tertiary-m button-text-m text-color-black"
                onClick={back}
                style={{ maxHeight: "32.5px" }}
              >
                {t("back")}
              </div>
            </div>
            {isOwner ? (
              <div className="container-row" style={{ alignItems: "center" }}>
                <div className="text-h3 text-color-black left-margin-s text-force-break">
                  {roomName}
                </div>
                <img
                  src={settingsIcon}
                  alt="Settings"
                  className="settings-icon"
                  style={{
                    marginBottom: "-7px",
                    marginLeft: "calc(var(--spacing-m) - 4px)",
                  }}
                  onClick={onOpenSettings}
                />
              </div>
            ) : (
              <div className="text-h3 text-color-black left-margin-s text-force-break">
                {roomName}
              </div>
            )}
          </div>

          <div
            className="button-primary black-fill button-text-l text-color-pink shadow-pink-opaque box-button-center top-margin-s"
            onClick={onStart}
            style={{
              opacity: isGenerating ? 0.5 : 1,
              pointerEvents: isGenerating ? "none" : "auto",
            }}
          >
            {startLabel}
          </div>

          <LobbyAccordions
            t={t}
            containerHeight={containerHeight}
            setRoomInput={setRoomInput}
            players={players}
            sessionId={sessionId}
            isOwner={isOwner}
            onAddName={addName}
            onUpdateName={updateName}
            onKick={kick}
          />
        </div>
        <div style={{ marginRight: -10 }}>
          <AddEntry
            value={roomInput}
            onChange={setRoomInput}
            onAdd={add}
            textareaRef={textArea}
            t={t}
          />
        </div>
      </div>
    </div>
  );
}

export default AIRoomLobby;
